import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { DynamicField } from '../../types';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { InfoIconFieldSelector } from './InfoIconFieldSelector';
import { FieldSelectorModal } from './modals/FieldSelectorModal';
import { EditFieldModal } from './modals/EditFieldModal';

interface FieldSelectorProps {
  fields: DynamicField[];
  selectedFieldId: string;
  onFieldChange: (fieldId: string) => void;
  onAddField: () => void;
  loading: boolean;
  error: any;
}

export const FieldSelector: React.FC<FieldSelectorProps> = ({
  fields,
  selectedFieldId,
  onFieldChange,
  onAddField,
  loading,
  error
}) => {
  const selectedField = fields.find(f => f.field_id === selectedFieldId);
  const [openFieldSelectorModal, setOpenFieldSelectorModal] = useState(false);
  const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false);
  const handleOpenFieldSelectorModal = () => {
    setOpenFieldSelectorModal(true);
  }

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = (
    message: string,
    severity: "success" | "error" | "warning" | "info" = "success"
  ) => {
    setNotification({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box className="field-selector">
        <Box className="field-selector-content">
          <CircularProgress size={24} />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Loading fields...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="field-selector">
        <Alert severity="error" sx={{ flex: 1 }}>
          Failed to load fields: {error.message || 'Unknown error'}
        </Alert>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddField}
          sx={{
            backgroundColor: 'primary.main',
            color: '#ffffff',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          Add Field
        </Button>
      </Box>
    );
  }

  return (
    <Box className="field-selector">
      <Box className="field-selector-content">
        <Box sx={{display: 'flex', alignItems: 'center', gap: '10px' , justifyContent: 'space-between'}}>
          <Box sx={{display: 'flex', alignItems: 'center' }}>
            <Typography 
              variant="h6" 
              component="label" 
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                minWidth: 'fit-content',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Select a Clinical Field
            </Typography>
            <InfoIconFieldSelector onClick={handleOpenFieldSelectorModal} />
          </Box>
            <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddField}
            sx={{
              marginLeft: '1.5rem',
              backgroundColor: 'primary.main',
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            Add Field
          </Button>
        </Box>
        
        <FormControl fullWidth focused={false} className="field-select" size="medium" sx={{mt: 2}}>
          <Select
            labelId="field-select-label"
            value={selectedFieldId}
            onChange={(e) => onFieldChange(e.target.value)}
            fullWidth
            sx={{
              borderRadius: '10px',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              color: 'text.primary',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {fields.map((field) => (
              <MenuItem key={field.field_id} value={field.field_id}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {field.field_name}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.primary',
                      display: 'block',
                      fontSize: '0.75rem'
                    }}
                  >
                    {/* {field.description} */}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
        sx={{
          backgroundColor: 'background.paper',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            backgroundColor: 'background.paper',
          },
          mt: 2,
          mb: 2,
          width: '100%'
        }}
        onClick={() => setIsEditFieldModalOpen(true)}
        >
          Edit field
        </Button>
      </Box>


      {/* Field Description */}
      {/* {selectedField && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <InfoOutlineIcon sx={{ color: 'primary.main' }} />
        </Box>
      )} */}

      <FieldSelectorModal
        open={openFieldSelectorModal}
        onClose={() => setOpenFieldSelectorModal(false)}
        selectedField={selectedField || null}
      />

     <EditFieldModal
        open={isEditFieldModalOpen}
        onClose={() => setIsEditFieldModalOpen(false)}
        onError={(error: string) => showNotification(error, "error")}
        fieldId={selectedFieldId}
      />
    </Box>
  );
};

 //   <Typography 
        //     variant="body2" 
        //     sx={{ 
        //       color: 'text.primary',
        //       fontStyle: 'italic',
        //       fontSize: '0.9rem'
        //     }}
        //   >
        //     Framework: {selectedField.description}
        //   </Typography>
        //   {selectedField.prompt_instructions && (
        //     <Typography 
        //       variant="caption" 
        //       sx={{ 
        //         color: 'text.primary',
        //         display: 'block',
        //         mt: 0.5,
        //         fontSize: '0.8rem'
        //       }}
        //     >
        //       Instructions: {selectedField.prompt_instructions.length > 100 
        //         ? `${selectedField.prompt_instructions.substring(0, 100)}...` 
        //         : selectedField.prompt_instructions}
        //     </Typography>
        //   )}
